import { useEffect, useMemo, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'
import { BranchModel, type BranchOption } from '@/http/models/Branch'
import { type Terminal, TerminalModel } from '@/http/models/Terminal'

/**
 * Branches and terminals for the POS history pages. Shifts and cash moves carry only `terminal_id` (no virtual to
 * the terminal), so names and branches are looked up here; a business has a handful of tills. Each list loads only
 * when the staff member may read it (a 403 would toast).
 */
export const usePosLookups = (enabled = true) => {
  const { can } = useAuth()
  const [branches, setBranches] = useState<BranchOption[]>([])
  const [terminals, setTerminals] = useState<Terminal[]>([])
  const canBranches = enabled && can('branches:list')
  const canTerminals = enabled && can('terminals:list')

  useEffect(() => {
    if (canBranches) BranchModel.getOptions().then((res) => res.success && res.data && setBranches(res.data.result))
  }, [canBranches])

  useEffect(() => {
    if (canTerminals) TerminalModel.getAll().then((res) => res.success && res.data && setTerminals(res.data.result))
  }, [canTerminals])

  return useMemo(() => {
    const terminalById = new Map(terminals.map((t) => [t._id, t]))
    const branchById = new Map(branches.map((b) => [b._id, b]))
    return {
      branches,
      terminals,
      terminalName: (id?: string | null) => (id ? (terminalById.get(id)?.name ?? '—') : '—'),
      branchOfTerminal: (id?: string | null) => (id ? (terminalById.get(id)?.branch?.name ?? '—') : '—'),
      branchName: (id?: string | null) => (id ? (branchById.get(id)?.name ?? '—') : '—'),
      /** Options for a terminal filter, labelled "Branch · Till". */
      terminalOptions: terminals.map((t) => ({ value: t._id, label: t.branch?.name ? `${t.branch.name} · ${t.name}` : t.name })),
    }
  }, [branches, terminals])
}
